import { BattleState, Monster, TurnCommands, TurnResult } from '../types';
import { AIConfig } from './types';
import { CommandWeightMap } from './weightedSelection';
import { evaluatePosition, POSITION_WEIGHTS } from './evaluator';
import { simulateTurnDeterministic } from './gameSimulator';
import { buildPairScores } from './aiSelector';
import { getTendencyBySpecies } from './tendencies';
import { getHpModifiers, getReflectorModifiers, getSelfReflectorModifiers } from './situationModifiers';
import { getAIConfigModifiers } from './aiConfigModifiers';
import { predictOpponentReplies } from './opponentModel';
import { selectViaMixedStrategy, ScoredCommand } from './mixedStrategy';

/**
 * Lv6 達人 AI の α-β ミニマックス探索エンジン
 *
 * 同時着手 TCB を「Stochastic Expectimax + α-β」モデルで近似する:
 * - 自分の候補ペア K 個（buildPairScores 上位）
 * - 各自分ペアに対し、相手の代表応手 M 個（Lv5 的予測）
 * - 各 (selfPair, oppPair) で 1 ターン進めて再帰
 * - 集約: score(selfPair) = α * min(child) + (1-α) * mean(child)
 *   - α は aiConfig.opponentModeling から導出（高いほど警戒、min 寄り）
 *
 * 純粋なミニマックスではないが、α-β 風の早期打ち切りで計算量を削減する。
 *
 * 設計判断（PR2 設計書 B-1 参照）:
 * - 同時着手をそのまま扱うために交互ノードにせず、自分視点で再帰
 * - Lv5 のフォールバック・パターン読みは探索内で陳腐化するが許容
 * - 葉評価は evaluator.ts、葉に到達するまでの遷移は simulateTurnDeterministic
 */

/** 自分の候補ペアの探索幅（ムーブオーダリング後の上位 K） */
const DEFAULT_K = 8;
/** 相手応手の代表数 M */
const DEFAULT_M = 3;

export interface AlphaBetaResult {
  /** 最善手のスコア */
  score: number;
  /** ルートで選ばれた最善手（再帰の中段では undefined） */
  bestCommand?: TurnCommands;
}

/**
 * Lv6 のメイン関数: 多ターン先読みで最善手を選ぶ
 *
 * @param state 現在のバトル状態
 * @param ownPlayerId 自分のプレイヤーID
 * @param ownMonster 自分のモンスター
 * @param opponentMonster 相手のモンスター
 * @param turnHistory ターン履歴（探索内では仮想ターンを追加しない）
 * @param aiConfig AI設定（lookAheadDepth, opponentModeling を使用）
 * @param randomFn 乱数関数（タイブレーク用、探索本体は決定論的）
 * @param bossSpecificMods （任意）ボス専用の重み調整。指定された場合、自分の候補ペア
 *                         スコアにこのマップを乗算する。通常 Lv6 使用時は undefined。
 *                         issue #264（ボス実装）で本格的に活用される。
 * @returns 選択された TurnCommands
 */
export function selectBestCommandsLv6(
  state: BattleState,
  ownPlayerId: 'player1' | 'player2',
  ownMonster: Monster,
  opponentMonster: Monster,
  turnHistory: TurnResult[],
  aiConfig: AIConfig,
  randomFn: () => number,
  bossSpecificMods?: CommandWeightMap,
): TurnCommands {
  const depth = Math.max(1, Math.min(5, aiConfig.lookAheadDepth));
  const opponentId: 'player1' | 'player2' = ownPlayerId === 'player1' ? 'player2' : 'player1';

  // ルート探索: 全 K 候補のスコアを集めて、最後に混合戦略で 1 手選ぶ
  const selfPairs = topKSelfPairs(state, ownPlayerId, ownMonster, opponentMonster, aiConfig, DEFAULT_K, bossSpecificMods);
  if (selfPairs.length === 0) {
    const fallback = predictOpponentReplies(state, ownPlayerId, ownMonster, opponentMonster, turnHistory, aiConfig, 1);
    return fallback[0];
  }

  const oppReplies = predictOpponentReplies(
    state, opponentId, opponentMonster, ownMonster, turnHistory, aiConfig, DEFAULT_M,
  );
  if (oppReplies.length === 0) {
    const fallback = predictOpponentReplies(state, ownPlayerId, ownMonster, opponentMonster, turnHistory, aiConfig, 1);
    return fallback[0];
  }

  const blendAlpha = aiConfig.opponentModeling / 100;
  const candidates: ScoredCommand[] = [];

  for (const selfPair of selfPairs) {
    let worstChild = Infinity;
    let sumChild = 0;
    let countChild = 0;

    for (const oppPair of oppReplies) {
      const p1Cmds = ownPlayerId === 'player1' ? selfPair : oppPair;
      const p2Cmds = ownPlayerId === 'player1' ? oppPair : selfPair;
      const monster1 = ownPlayerId === 'player1' ? ownMonster : opponentMonster;
      const monster2 = ownPlayerId === 'player1' ? opponentMonster : ownMonster;
      const { newState } = simulateTurnDeterministic(state, monster1, monster2, p1Cmds, p2Cmds);

      const childResult = alphabeta(
        newState, ownPlayerId, ownMonster, opponentMonster, turnHistory,
        depth - 1, -Infinity, Infinity, aiConfig, randomFn,
      );
      worstChild = Math.min(worstChild, childResult.score);
      sumChild += childResult.score;
      countChild += 1;
    }

    const meanChild = sumChild / countChild;
    const aggregated = blendAlpha * worstChild + (1 - blendAlpha) * meanChild;
    candidates.push({ command: selfPair, score: aggregated });
  }

  // PR5: 混合戦略で最終選択（mixedStrategy=0 なら argmax と同等）
  return selectViaMixedStrategy(candidates, aiConfig.mixedStrategy, randomFn);
}

/**
 * α-β ミニマックス再帰本体
 *
 * @returns score（自分視点）と、ルートのみ bestCommand
 */
export function alphabeta(
  state: BattleState,
  ownPlayerId: 'player1' | 'player2',
  ownMonster: Monster,
  opponentMonster: Monster,
  turnHistory: TurnResult[],
  depth: number,
  alpha: number,
  beta: number,
  aiConfig: AIConfig,
  randomFn: () => number,
): AlphaBetaResult {
  // 終端条件: depth=0 または KO
  const opponentId: 'player1' | 'player2' = ownPlayerId === 'player1' ? 'player2' : 'player1';
  const ownState = state[ownPlayerId];
  const oppState = state[opponentId];

  if (ownState.currentHp <= 0 || oppState.currentHp <= 0 || depth === 0) {
    return { score: evaluatePosition(state, ownPlayerId, ownMonster, opponentMonster, aiConfig) };
  }

  // 自分の候補ペアを K 個に絞る
  const selfPairs = topKSelfPairs(state, ownPlayerId, ownMonster, opponentMonster, aiConfig, DEFAULT_K);
  if (selfPairs.length === 0) {
    // 有効な手なし（防御的）
    return { score: evaluatePosition(state, ownPlayerId, ownMonster, opponentMonster, aiConfig) };
  }

  // 相手の応手 M 個（探索ノード内では決定的に固定）
  const oppReplies = predictOpponentReplies(
    state,
    opponentId,
    opponentMonster,
    ownMonster,
    turnHistory,
    aiConfig,
    DEFAULT_M,
  );
  if (oppReplies.length === 0) {
    return { score: evaluatePosition(state, ownPlayerId, ownMonster, opponentMonster, aiConfig) };
  }

  // ハイブリッド α: opponentModeling を 0..1 にマップ
  const blendAlpha = aiConfig.opponentModeling / 100;

  let bestScore = -Infinity;
  let bestSelfPair: TurnCommands | undefined;
  let currentAlpha = alpha;

  for (const selfPair of selfPairs) {
    let worstChild = Infinity;
    let sumChild = 0;
    let countChild = 0;

    for (const oppPair of oppReplies) {
      // 1 ターン進行（決定的、必中版）
      const p1Cmds = ownPlayerId === 'player1' ? selfPair : oppPair;
      const p2Cmds = ownPlayerId === 'player1' ? oppPair : selfPair;
      const monster1 = ownPlayerId === 'player1' ? ownMonster : opponentMonster;
      const monster2 = ownPlayerId === 'player1' ? opponentMonster : ownMonster;

      const { newState } = simulateTurnDeterministic(state, monster1, monster2, p1Cmds, p2Cmds);

      // 再帰: 同じ自分視点で次の depth へ
      const childResult = alphabeta(
        newState,
        ownPlayerId,
        ownMonster,
        opponentMonster,
        turnHistory,
        depth - 1,
        currentAlpha,
        beta,
        aiConfig,
        randomFn,
      );

      worstChild = Math.min(worstChild, childResult.score);
      sumChild += childResult.score;
      countChild += 1;

      // β剪定: 既にこの selfPair が β を超えるほど悪化していれば打ち切り
      if (worstChild < currentAlpha) break;
    }

    // 相手応手集約: ハイブリッド（min と mean のブレンド）
    const meanChild = sumChild / countChild;
    const aggregated = blendAlpha * worstChild + (1 - blendAlpha) * meanChild;

    if (aggregated > bestScore) {
      bestScore = aggregated;
      bestSelfPair = selfPair;
    }

    currentAlpha = Math.max(currentAlpha, bestScore);
    if (currentAlpha >= beta) break; // α剪定
  }

  return { score: bestScore, bestCommand: bestSelfPair };
}

/**
 * 自分の候補ペアを K 個に絞る（ムーブオーダリング）
 *
 * buildPairScores のスコア上位を抽出して TurnCommands 配列に変換する。
 * α-β の効率は良い手順で探索することで決まるため、ここの精度が探索性能を左右する。
 */
function topKSelfPairs(
  state: BattleState,
  ownPlayerId: 'player1' | 'player2',
  ownMonster: Monster,
  opponentMonster: Monster,
  aiConfig: AIConfig,
  k: number,
  bossSpecificMods?: CommandWeightMap,
): TurnCommands[] {
  const opponentId: 'player1' | 'player2' = ownPlayerId === 'player1' ? 'player2' : 'player1';
  const ownState = state[ownPlayerId];
  const oppState = state[opponentId];

  const speciesTendency = getTendencyBySpecies(ownMonster.id);
  const ownHpRatio = ownState.currentHp / ownMonster.stats.hp;
  const oppHpRatio = oppState.currentHp / opponentMonster.stats.hp;
  const hpMods = getHpModifiers(ownHpRatio, oppHpRatio);
  const reflectorMods = getReflectorModifiers(
    Math.max(0, opponentMonster.reflector.maxReflectCount - oppState.usedReflectCount),
  );
  const selfReflectorMods = getSelfReflectorModifiers(
    Math.max(0, ownMonster.reflector.maxReflectCount - ownState.usedReflectCount),
  );
  const configMods = getAIConfigModifiers(aiConfig);

  const pairScores = buildPairScores(
    state,
    ownPlayerId,
    ownMonster,
    opponentMonster,
    speciesTendency,
    hpMods,
    reflectorMods,
    undefined, undefined, undefined, undefined, undefined, undefined,
    selfReflectorMods,
    configMods,
  );

  // ボス専用モディファイアが指定されていれば、ペアスコアに乗算で適用
  // （1st と 2nd の両方の重みを掛ける）
  let entries = Array.from(pairScores.values());
  if (bossSpecificMods) {
    entries = entries.map((entry) => ({
      ...entry,
      score: entry.score
        * (bossSpecificMods[entry.first] ?? 1.0)
        * (bossSpecificMods[entry.second] ?? 1.0),
    }));
  }

  const sorted = entries.sort((a, b) => b.score - a.score);
  return sorted.slice(0, k).map((entry) => ({
    first: { type: entry.first },
    second: { type: entry.second },
  }));
}
